import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Container, Typography } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import { useTranslation } from "react-i18next";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomCard from "../../components/Famer/GenericCard";
import ConfirmationPopup from "../../components/Popup/GenericPopup";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../components/Alert";
import { getAuth, saveAuth, saveToLocal } from "../../globals/authentication";

export default function VLEList() {
  const [vles, setVLEs] = useState([]);
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedVLEId, setSelectedVLEId] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  useEffect(() => {
    fetchVLEs();
  }, []);

  const fetchVLEs = async () => {
    try {
      const response = await api.get("/areamanager/vles-list/");
      setVLEs(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching VLEs:", error);
      setLoading(false);
      showAlert("error", "Error fetching VLEs");
    }
  };

  const handleDelete = (id) => {
    setSelectedVLEId(id);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/auth/delete-account/?user_id=${selectedVLEId}`);
      showAlert("success", "VLE Deleted Successfully");
      fetchVLEs();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error deleting the VLE";
      showAlert("error", errorMessage);
    }
    setDeleteConfirmationOpen(false);
  };

  const handleEditClick = (vleId) => {
    navigate(`/areamanager/vles/edit/?vleId=${vleId}`);
  };

  const handleAddVLE = () => {
    navigate("/area-manager/vle/add");
  };

  const handleLoginVLE = async (id) => {
    try {
      const currentAuthData = getAuth();

      const response = await api.post(
        `/areamanager/vle-login/?persona_user=${id}`
      );
      const authData = JSON.stringify(response.data);

      if (currentAuthData && currentAuthData.is_internal_login) {
        navigate(`/login-internal/?authDetails=${authData}`, { replace: true });
      } else {
        if (currentAuthData) {
          saveToLocal("auth_backup", currentAuthData);
        }
        saveAuth(authData);
        navigate(`/login-internal/?authDetails=${authData}`, { replace: true });
      }
    } catch (error) {
      console.error("Error logging in as VLE:", error);
      showAlert("error", "Error logging in as VLE");
    }
  };

  return (
    <>
      <Helmet>
        <title>My VLEs - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{
            height: vles.length === 0 ? "58vh" : "94vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {vles.length !== 0 && <CustomTitle>{t("My VLEs")}</CustomTitle>}
          <div
            style={{
              overflowY: "auto",
              maxHeight: "calc(115vh - 300px)",
              textAlign: "center",
              height: "700px",
              margin: "auto",
            }}
          >
            {vles.map((vle) => (
              <CustomCard
                key={vle.id}
                id={vle.id}
                t={t}
                data={{
                  [t("Name")]: vle.full_name,
                  [t("Username")]: vle.username,
                  [t("Email")]: vle.email,
                  [t("Phone")]: vle.phone_number,
                  [t("Address")]: vle.address,
                }}
                handleEditClick={() => handleEditClick(vle.id)}
                handleDelete={() => handleDelete(vle.id)}
                handlePrimaryButton={() => handleLoginVLE(vle.id)}
                primaryLabel="Login"
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CustomButton onClick={handleAddVLE}>{t("Add VLE")}</CustomButton>
            {vles.length === 0 && (
              <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
                {t("Your VLEs will be shown here once you add them.")}
              </Typography>
            )}
          </div>
        </Container>
      </LoaderContainer>
      <ConfirmationPopup
        open={deleteConfirmationOpen}
        handleClose={() => setDeleteConfirmationOpen(false)}
        handleConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this VLE?"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
}
